import * as React from "react";
import { useMutation } from "react-query";
import { ApiKeyMtlsCertificate, ApiKeyMtlsCertificateUpdateRequest, ApiKeysService } from "gen/clients";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SwitchField } from "components/formikFields/SwitchField/SwitchField";

interface Props {
  keyId: string;
  certificate: ApiKeyMtlsCertificate;
  onClose: () => void;
  onSuccess: () => void;
}

enum FieldName {
  description = "description",
  enabled = "enabled"
}

interface FormValues {
  [FieldName.description]: string;
  [FieldName.enabled]: boolean;
}

const validationSchema = Yup.object().shape({
  [FieldName.description]: Yup.string(),
  [FieldName.enabled]: Yup.boolean()
});

const EditMtlsCertificateDialog: React.FC<Props> = ({ keyId, certificate, onClose, onSuccess }) => {
  const { mutateAsync, error, isLoading } = useMutation(ApiKeysService.updateApiKeyMtlsCertificate, {
    onSuccess
  });

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const request: ApiKeyMtlsCertificateUpdateRequest = {
        description: values.description || undefined,
        enabled: values.enabled
      };
      await mutateAsync({ keyId, certificateId: certificate.id, requestBody: request });
    },
    [keyId, certificate.id, mutateAsync]
  );

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle>Edit Certificate</DialogTitle>
      <Formik
        initialValues={{
          [FieldName.description]: certificate.description || "",
          [FieldName.enabled]: certificate.enabled
        }}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form noValidate={true}>
            <DialogContent>
              <Stack spacing={3}>
                <TextInputField
                  name={FieldName.description}
                  label="Description"
                  helperText="Optional description of this certificate"
                />
                <SwitchField
                  name={FieldName.enabled}
                  label="Enabled"
                  helperText="Whether this certificate binding is active"
                />
                {!!error && <ApiErrorMessage apiError={error} />}
              </Stack>
            </DialogContent>
            <DialogActions>
              <LoadingButton variant="contained" isLoading={isSubmitting || isLoading}>
                Save Changes
              </LoadingButton>
              <Button onClick={onClose} disabled={isSubmitting || isLoading}>
                Cancel
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { EditMtlsCertificateDialog };
